/**
 * Language Context
 * ================
 * Provides i18n support via React Context.
 * Follows the same pattern as AuthContext.
 *
 * Usage:
 * <LanguageProvider translations={allTranslations}>
 *   <App />
 * </LanguageProvider>
 *
 * In components:
 * const { t, lang, setLang } = useLang();
 *
 * Marketing handoff: `?lang=en|de|tr` overrides saved preference on load,
 * then is stripped from the URL via React Router replace (see langFromUrl.ts).
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
  ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lang, Translations } from "./types";
import {
  hasLangQueryParam,
  readLangFromUrl,
  searchWithoutLangParam,
} from "./langFromUrl";

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

interface LanguageProviderProps {
  children: ReactNode;
  translations: Translations;
}

const STORAGE_KEY = "app_lang";

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

function persistLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

/**
 * Detect initial language:
 * 1. Valid `?lang=` (marketing handoff — overrides saved preference)
 * 2. localStorage `app_lang`
 * 3. Browser language (tr / de)
 * 4. English
 */
export function getInitialLang(): Lang {
  const fromUrl = readLangFromUrl();
  if (fromUrl) {
    persistLang(fromUrl);
    return fromUrl;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "tr" || stored === "en" || stored === "de") return stored;
  } catch {
    /* SSR or restricted storage */
  }

  if (typeof navigator !== "undefined") {
    if (navigator.language?.startsWith("tr")) return "tr";
    if (navigator.language?.startsWith("de")) return "de";
  }

  return "en";
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  translations,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    persistLang(newLang);
  }, []);

  // Consume `?lang=` on full load and in-app navigations; strip via RR replace.
  useLayoutEffect(() => {
    if (!hasLangQueryParam(location.search)) return;

    const fromUrl = readLangFromUrl(location.search);
    if (fromUrl) {
      setLang(fromUrl);
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchWithoutLangParam(location.search),
        hash: location.hash,
      },
      { replace: true },
    );
  }, [
    location.pathname,
    location.search,
    location.hash,
    navigate,
    setLang,
  ]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key: string): string => translations[lang]?.[key] ?? key,
    [lang, translations],
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLang must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageProvider;
